import type { AnalyticsConfig } from '@carboncopyco/types/desktop/analytics';
import { PositionedToggle } from '@carboncopyco/ui';
import { useObjectState } from '@uidotdev/usehooks';
import { useCallback, useEffect, useState } from 'react';
import useElectronListener from '../../../hooks/useElectronListener';
import { useTabs } from '../../../store/context';
import { LogsTable } from './LogsTable';
import { MAIN_TO_RENDERER_MONITORING_ANALYTICS_LOG_DATA } from './consts';
import type { AnalyticsApiLog } from './types';

type LogState = Record<string, AnalyticsApiLog[]>;

const initState: LogState = {
  '1': [
    {
      timestamp: '1',
      analyticsSessionId: '1',
      message: 'no analytics',
      data: { analyticsSessionId: '1', timestamp: '1' },
    },
  ],
};

export const AnalyticsMonitoring = () => {
  const [logs, setLogs] = useObjectState(initState);

  const { globalTabs, activeGlobalTabId } = useTabs();
  const activeTab = globalTabs.filter(({ id }) => id === activeGlobalTabId)[0];

  const [analyticsConfig, setAnalyticsConfig] = useState<AnalyticsConfig>();
  const [isTestConfig, setIsTestConfig] = useState(false);

  const getLogFromAnalyticsApi = useCallback(
    (...args: unknown[]) => {
      const log = args[0] as unknown as AnalyticsApiLog;
      if (isTestConfig && log?.analyticsSessionId) {
        console.log(log);
      }
      const sessionLogs = logs[log.analyticsSessionId]
        ? [...logs[log.analyticsSessionId], log]
        : [log];

      if (sessionLogs.length > 300) {
        sessionLogs.shift();
      }

      setLogs({
        [log.analyticsSessionId]: sessionLogs,
      });
    },
    [logs]
  );

  const getAnalyticsConfig = useCallback(() => {
    window.dsDesktop.analyticsClient
      .isDebug()
      .then(setIsTestConfig)
      .catch((error) => console.log('couldnt find analytics store', error));
    window.dsDesktop.analyticsClient.config
      .get()
      .then(setAnalyticsConfig)
      .catch((error) => console.log('couldnt find analytics store', error));
  }, []);

  useEffect(() => {
    getAnalyticsConfig();
  }, []);

  useElectronListener(
    MAIN_TO_RENDERER_MONITORING_ANALYTICS_LOG_DATA,
    getLogFromAnalyticsApi,
    []
  );

  const configEntries = analyticsConfig
    ? [...Object.entries(analyticsConfig)].sort((a, b) => b[1] - a[1])
    : [];

  return (
    <>
      <hr className="my-6" />

      <div className="flex flex-row m-2">
        <table className="text-sm text-left text-gray-500 dark:text-gray-400 mt-4">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th
                scope="col"
                className="px-6 py-3"
              >
                Config
              </th>
              <th
                scope="col"
                className="px-6 py-3"
              >
                Value
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
              <th
                scope="row"
                className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
              >
                Debug
              </th>
              <td className="px-6 py-4">
                <PositionedToggle
                  onChange={() => {
                    if (!isTestConfig) {
                      window.dsDesktop.analyticsClient.config.test();
                      getAnalyticsConfig();
                      return;
                    }

                    window.dsDesktop.analyticsClient.config.reset();
                    getAnalyticsConfig();
                  }}
                  name="Test config"
                  checked={isTestConfig}
                />
              </td>
            </tr>
            {configEntries.map(([key, val]) => {
              return (
                <tr
                  key={key}
                  className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
                >
                  <th
                    scope="row"
                    className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                  >
                    {key}
                  </th>
                  <td className="px-6 py-4">{val / 1000}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="m-2" />
        <table className="text-sm text-left text-gray-500 dark:text-gray-400 mt-4">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th
                scope="col"
                className="px-6 py-3"
              >
                Context
              </th>
              <th
                scope="col"
                className="px-6 py-3"
              />
            </tr>
          </thead>
          <tbody>
            <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
              <th className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                Analytics session Id
              </th>
              <td className="px-6 py-4">{activeTab?.analyticsSessionId}</td>
            </tr>
            <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
              <th className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                Tab id
              </th>
              <td className="px-6 py-4">{activeTab?.id}</td>
            </tr>
            <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
              <th className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                PresentationId
              </th>
              <td className="px-6 py-4">{activeTab?.presentationId}</td>
            </tr>
            <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
              <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                PresentationType
              </td>
              <td className="px-6 py-4">{activeTab?.presentationType}</td>
            </tr>
            <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
              <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                PlayId
              </td>
              <td className="px-6 py-4">{activeTab?.playId}</td>
            </tr>
          </tbody>
        </table>
        <div className="m-2" />

        {activeTab?.analyticsSessionId &&
          logs[activeTab?.analyticsSessionId] && (
            <LogsTable logs={logs[activeTab?.analyticsSessionId]} />
          )}
      </div>
    </>
  );
};
